const STRIP_TAGS_RE = /<\/?[^>]+(>|$)/g;

/**
 * Extracts an email subject from HTML content.
 *
 * Looks for a "Subject: " prefix inside a block element (<p> or <div>)
 * or as plain text. Strips tags from the subject text and removes the
 * subject block from the content body.
 *
 * @param {string} content - HTML string to search
 * @param {string} subjectPrefix - Prefix to look for (e.g. "Subject: ")
 * @returns {{ content: string, subject: string|null }}
 */
const extractSubject = (content, subjectPrefix) => {
  if (!content.includes(subjectPrefix)) {
    return { content, subject: null };
  }

  // Match subject wrapped in a block element: <p>Subject: text</p> or <div>Subject: text</div>
  // Handles nested inline tags like <strong> before/around the prefix.
  const escaped = subjectPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const blockRe = new RegExp(
    `<(p|div)\\b[^>]*>\\s*(?:<[^>]*>\\s*)*${escaped}([\\s\\S]*?)<\\/\\1>`,
    "i",
  );
  const blockMatch = content.match(blockRe);

  if (blockMatch) {
    const subject = blockMatch[2].replace(STRIP_TAGS_RE, "").trim();
    const cleanedContent = content.replace(blockMatch[0], "").trim();
    return { content: cleanedContent, subject };
  }

  // Fallback: plain text (no wrapping tag).
  // Subject ends at the first newline or HTML tag after the prefix.
  const idx = content.indexOf(subjectPrefix);
  const afterPrefix = idx + subjectPrefix.length;
  const nlIdx = content.indexOf("\n", idx);
  const tagIdx = content.indexOf("<", afterPrefix);

  let subjectEnd;
  let contentStart;
  if (nlIdx >= 0 && (tagIdx < 0 || nlIdx <= tagIdx)) {
    // Newline is the delimiter
    subjectEnd = nlIdx;
    contentStart = nlIdx + 1;
  } else if (tagIdx >= 0) {
    // HTML tag is the delimiter (no newline before it)
    subjectEnd = tagIdx;
    contentStart = tagIdx;
  } else {
    // No delimiter — entire remaining text is the subject
    subjectEnd = content.length;
    contentStart = content.length;
  }

  const subject = content
    .slice(afterPrefix, subjectEnd)
    .replace(STRIP_TAGS_RE, "")
    .trim();
  const cleanedContent = (
    content.slice(0, idx) + content.slice(contentStart)
  ).trim();

  return { content: cleanedContent, subject };
};

export default extractSubject;
